// @ts-ignore - This import is needed for Supabase migration
import { supabase } from '@/lib/supabase';

import { formatDateTime } from '@/lib/dateUtils';
import { ArrowDownRight, ArrowRightLeft, ArrowUpRight, Receipt, Paperclip, PiggyBank } from 'lucide-react';
import { type Transaction, type TransferDetails } from '@/types/transaction';
import { useAccounts } from '@/store/accountsStore';
import { useTagsStore } from '@/store/tagsStore';
import { useCategoriesStore } from '@/store/categoriesStore';
import { formatCurrency } from '@/utils/formatters';
// Migrated: Using native Date instead of Firebase Timestamp
// Migrated: Using native Date instead of Firebase Timestamp
import { cn } from '@/lib/utils';
import { Badge } from '../ui/badge';
import { useCurrenciesStore } from '@/store/currenciesStore';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useState } from 'react';
import TransactionDetailsModal from './TransactionDetailsModal';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSettingsStore } from '@flows.cash/core';
import { Transaction as BaseTransaction } from '@/types/base';
import AssignToGoalDialog from './AssignToGoalDialog';

// Define Timestamp interface for compatibility
interface Timestamp {
  toDate(): Date;
}

// Extended Transaction interface to include additional fields
interface ExtendedTransaction extends BaseTransaction {
  to_account_id?: string;
  toAccountId?: string;
  metadata?: {
    isTransfer?: boolean;
    fromAccountId?: string;
    toAccountId?: string;
    fromAccountName?: string;
    toAccountName?: string;
    direction?: 'in' | 'out';
  };
  transferDetails?: TransferDetails;
}

interface TransactionCardProps {
  transaction: Transaction;
}

export default function TransactionCard({ transaction }: TransactionCardProps) {
  // Type assertion to use the extended interface
  const extendedTransaction = transaction as unknown as ExtendedTransaction;
  
  const { accounts } = useAccounts();
  const { tags } = useTagsStore();
  const { categories } = useCategoriesStore();
  const { currencies } = useCurrenciesStore();
  const { settings } = useSettingsStore();
  const isMobile = useMediaQuery('(max-width: 768px)');
  
  const account = accounts.find((a: any) => a.id === extendedTransaction.accountId);
  const currencyCode = account?.currency || 'USD';
  // Find the currency object from the currencies array
  const currency = currencies.find(c => c.code === currencyCode);

  // Check if the transaction is a transfer
  const isTransferTransaction = extendedTransaction.type === 'transfer' || 
                                extendedTransaction.transferDetails?.isTransfer === true ||
                                extendedTransaction.to_account_id !== undefined ||
                                extendedTransaction.metadata?.isTransfer === true;

  // Get linked account for transfers
  const linkedAccountId = isTransferTransaction 
    ? (
        extendedTransaction.to_account_id || 
        extendedTransaction.toAccountId || 
        (extendedTransaction.amount < 0 
          ? extendedTransaction.transferDetails?.toAccountId 
          : extendedTransaction.transferDetails?.fromAccountId) ||
        (extendedTransaction.amount < 0 
          ? extendedTransaction.metadata?.toAccountId 
          : extendedTransaction.metadata?.fromAccountId)
      )
    : null;

  const linkedAccount = linkedAccountId 
    ? accounts.find((a: any) => a.id === linkedAccountId) 
    : null;

  const getCategoryInfo = () => {
    // If it's a transfer, return transfer category
    if (isTransferTransaction) {
      const transferDirection = extendedTransaction.amount < 0 
        || extendedTransaction.transferDetails?.direction === 'out'
        || extendedTransaction.metadata?.direction === 'out'
        ? 'out' 
        : 'in';
      
      return { 
        name: `Transfer ${transferDirection === 'out' ? 'to' : 'from'} ${linkedAccount?.name || 'account'}`, 
        color: '#6366F1', // Indigo color for transfers
        isDefault: false,
        isTransfer: true
      };
    }
    
    if (!extendedTransaction.categoryId) return { name: 'Uncategorized', color: '#A3A3A3', isDefault: true };
    
    // Try both ':' and '/' as separators
    let mainCatId, subCatId;
    if (extendedTransaction.categoryId.includes(':')) {
      [mainCatId, subCatId] = extendedTransaction.categoryId.split(':');
    } else if (extendedTransaction.categoryId.includes('/')) {
      [mainCatId, subCatId] = extendedTransaction.categoryId.split('/');
    } else {
      // If no separator found, the whole ID might be the subcategory ID
      subCatId = extendedTransaction.categoryId;
    }

    if (!subCatId) return { name: 'Uncategorized', color: '#A3A3A3', isDefault: true };

    // First try to find by subcategory ID directly
    for (const mainCategory of categories) {
      const subcategory = mainCategory.subcategories.find(sub => sub.id === subCatId);
      if (subcategory) {
        return { 
          name: subcategory.name, 
          color: subcategory.color || mainCategory.color, 
          isDefault: false 
        };
      }
    }

    // If not found and we have mainCatId, try with main category
    if (mainCatId) {
      const mainCategory = categories.find(cat => cat.id === mainCatId);
      if (mainCategory) {
        const subcategory = mainCategory.subcategories.find(sub => sub.id === subCatId);
        if (subcategory) {
          return { 
            name: subcategory.name, 
            color: subcategory.color || mainCategory.color, 
            isDefault: false 
          };
        }
      }
    }

    return { name: extendedTransaction.category || 'Uncategorized', color: '#A3A3A3', isDefault: true };
  };

  const categoryInfo = getCategoryInfo();

  const formatAmount = (amount: number) => {
    // Use the user's preferred number format from settings
    const numberFormat = settings?.number_format || '#,##0.00';
    
    // Only pass the supported properties to formatCurrency
    return formatCurrency(amount, currency, {
      showSymbol: true,
      showCode: true,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      numberFormat: numberFormat
    });
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);

  return (
    <>
      <div 
        className="group bg-white dark:bg-[#0E131E] rounded-xl border border-neutral-200 dark:border-[#333840] p-2 sm:p-4 hover:shadow-lg transition-all duration-200 cursor-pointer"
        onClick={() => setIsModalOpen(true)}
      >
        {/* Mobile View */}
        <div className="block sm:hidden">
          {/* Title */}
          <div className="mb-2">
            <h3 className="text-sm font-semibold text-neutral-900 dark:text-[#E8E8E8] truncate">
              {extendedTransaction.title || extendedTransaction.description || <span className="text-sm text-gray-400 dark:text-gray-500">No Description</span>}
            </h3>
          </div>

          {/* Amount */}
          <div className="flex items-center gap-2 mb-1">
            {isTransferTransaction ? (
              <ArrowRightLeft className="w-4 h-4 text-[#6366F1]" />
            ) : extendedTransaction.type === 'expense' ? (
              <ArrowDownRight className="w-4 h-4 text-[#E53935]" />
            ) : (
              <ArrowUpRight className="w-4 h-4 text-[#4CAF50]" />
            )}
            <span className={cn(
              "font-semibold",
              extendedTransaction.amount < 0 ? 'text-[#E53935]' : 'text-[#4CAF50]'
            )}>
              {extendedTransaction.amount < 0 ? '-' : '+'}
              {formatAmount(Math.abs(extendedTransaction.amount))}
            </span>
          </div>

          {/* Date and Attachment Icon */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-neutral-500 dark:text-neutral-400">
              {formatDateTime(extendedTransaction.date)}
            </span>
            {extendedTransaction.attachments && extendedTransaction.attachments.length > 0 && (
              <Paperclip className="w-4 h-4 text-gray-400" />
            )}
          </div>

          {/* Category and Account */}
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            <Badge 
              variant="outline"
              className={cn(
                "font-medium text-xs",
                categoryInfo.isDefault && "text-gray-400 dark:text-gray-500 italic"
              )}
              style={{ 
                backgroundColor: `${categoryInfo.color}10`,
                color: categoryInfo.isDefault ? undefined : categoryInfo.color,
                borderColor: `${categoryInfo.color}30`
              }}
            >
              {categoryInfo.name}
            </Badge>
            {account && (
              <Badge 
                variant="secondary"
                className="bg-neutral-100 dark:bg-[#0E131E] text-neutral-700 dark:text-[#E8E8E8] border-neutral-200 dark:border-[#333840] font-medium text-xs"
              >
                {account.name}
              </Badge>
            )}
          </div>
        </div>

        {/* Desktop View */}
        <div className="hidden sm:flex sm:items-start gap-4">
          {/* Bottom section with date */}
          <div className="flex items-center justify-between mt-2 text-sm text-gray-500">
            <span>{formatDateTime(extendedTransaction.date)}</span>
          </div>

          {/* Icon */}
          <div className="hidden sm:block flex-shrink-0">
            <div 
              className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center",
                isTransferTransaction 
                  ? 'bg-[#6366F1]/10 dark:bg-[#6366F1]/10 text-[#6366F1] dark:text-[#6366F1]'
                  : extendedTransaction.type === 'expense' 
                    ? 'bg-[#E53935]/10 dark:bg-[#E53935]/10 text-[#E53935] dark:text-[#E53935]' 
                    : 'bg-[#4CAF50]/10 dark:bg-[#4CAF50]/10 text-[#4CAF50] dark:text-[#4CAF50]'
              )}
            >
              {isTransferTransaction ? (
                <ArrowRightLeft className="w-5 h-5" />
              ) : extendedTransaction.type === 'expense' ? (
                <ArrowDownRight className="w-5 h-5" />
              ) : (
                <ArrowUpRight className="w-5 h-5" />
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-grow min-w-0">
            {/* Desktop Header - Date */}
            <div className="hidden sm:flex items-center gap-2 mb-2">
              <span className="text-sm text-neutral-600 dark:text-[#A3A3A3] font-medium">
                {(() => {
                  try {
                    if (!extendedTransaction.date) return '';
                    
                    // Handle different date types
                    let dateToFormat: Date;
                    
                    if (typeof extendedTransaction.date === 'object' && extendedTransaction.date !== null && 'toDate' in extendedTransaction.date) {
                      // Handle Timestamp-like objects
                      dateToFormat = (extendedTransaction.date as unknown as Timestamp).toDate();
                    } else if (extendedTransaction.date instanceof Date) {
                      dateToFormat = extendedTransaction.date;
                    } else {
                      // Handle string or number
                      dateToFormat = new Date(extendedTransaction.date);
                    }
                    
                    return formatDateTime(dateToFormat, {
                      dateFormat: settings?.date_format,
                      timeFormat: settings?.time_format
                    });
                  } catch (error) {
                    console.error('Error formatting date:', error, extendedTransaction.date);
                    return 'Invalid date';
                  }
                })()}
              </span>
              {extendedTransaction.hasAttachment && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <Receipt className="w-4 h-4 text-[#2962FF] dark:text-[#2962FF]" />
                    </TooltipTrigger>
                    <TooltipContent className="bg-white dark:bg-[#0E131E] text-neutral-900 dark:text-[#E8E8E8]">
                      Has receipt
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>

            {/* Title/Description */}
            <div className="space-y-1">
              <h3 className="text-sm sm:text-base font-semibold text-neutral-900 dark:text-[#E8E8E8] truncate">
                {extendedTransaction.title || extendedTransaction.description || <span className="text-sm text-gray-400 dark:text-gray-500">No Description</span>}
              </h3>
              {/* Only show description on desktop if different from title */}
              {!isMobile && extendedTransaction.description && extendedTransaction.description !== extendedTransaction.title && (
                <p className="text-sm text-neutral-600 dark:text-[#A3A3A3] truncate">
                  {extendedTransaction.description}
                </p>
              )}
            </div>

            {/* Category and Account */}
            <div className="flex flex-wrap items-center justify-between mt-1 sm:mt-2">
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                <Badge 
                  variant="outline"
                  className={cn(
                    "font-medium text-xs sm:text-sm",
                    categoryInfo.isDefault && "text-gray-400 dark:text-gray-500 italic"
                  )}
                  style={{ 
                    backgroundColor: `${categoryInfo.color}10`,
                    color: categoryInfo.isDefault ? undefined : categoryInfo.color,
                    borderColor: `${categoryInfo.color}30`
                  }}
                >
                  {categoryInfo.name}
                </Badge>
                {account && (
                  <Badge 
                    variant="secondary"
                    className="bg-neutral-100 dark:bg-[#0E131E] text-neutral-700 dark:text-[#E8E8E8] border-neutral-200 dark:border-[#333840] font-medium text-xs sm:text-sm"
                  >
                    {account.name}
                  </Badge>
                )}
              </div>
              {extendedTransaction.attachments && extendedTransaction.attachments.length > 0 && (
                <Paperclip className="w-4 h-4 text-gray-400" />
              )}
            </div>

            {/* Tags - Only show on desktop */}
            {!isMobile && extendedTransaction.tags && extendedTransaction.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {extendedTransaction.tags.map((tag) => {
                  const tagData = typeof tag === 'string' 
                    ? tags.find(t => t.id === tag)
                    : tag;

                  if (!tagData) return null;
                  
                  return (
                    <Badge
                      key={tagData.id}
                      variant="outline"
                      className="text-xs font-medium"
                      style={{ 
                        backgroundColor: `${tagData.color}10`,
                        color: tagData.color,
                        borderColor: `${tagData.color}30`
                      }}
                    >
                      {tagData.name}
                    </Badge>
                  );
                })}
              </div>
            )}

            {/* Notes - Only show on desktop */}
            {!isMobile && extendedTransaction.notes && (
              <div className="text-sm text-neutral-600 dark:text-[#A3A3A3] whitespace-pre-wrap mt-2 bg-neutral-50 dark:bg-[#0E131E] rounded-lg p-2 font-medium border border-neutral-200 dark:border-[#333840]">
                {extendedTransaction.notes}
              </div>
            )}
          </div>

          {/* Desktop Amount */}
          <div className="hidden sm:block flex-shrink-0">
            <span 
              className={cn(
                "text-base font-semibold whitespace-nowrap",
                extendedTransaction.amount < 0 ? 'text-[#E53935] dark:text-[#E53935]' : 'text-[#4CAF50] dark:text-[#4CAF50]'
              )}
            >
              {extendedTransaction.amount < 0 ? '-' : '+'}
              {formatAmount(Math.abs(extendedTransaction.amount))}
            </span>
          </div>
        </div>

        {/* Add assign to goal button in the action section */}
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-neutral-100 dark:border-[#333840]">
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            {formatDateTime(new Date(extendedTransaction.date), {})}
          </span>
          
          <div className="flex items-center gap-2">
            {extendedTransaction.attachments && extendedTransaction.attachments.length > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-neutral-500 dark:text-neutral-400 flex items-center">
                      <Paperclip className="w-3 h-3" />
                      <span className="ml-1 text-xs">{extendedTransaction.attachments.length}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{extendedTransaction.attachments.length} attachments</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            
            <button
              className="text-neutral-500 hover:text-primary dark:text-neutral-400 dark:hover:text-primary-light text-xs flex items-center transition-colors duration-200"
              onClick={(e) => {
                e.stopPropagation(); // Prevent opening the details modal
                setIsAssignDialogOpen(true);
              }}
            >
              <PiggyBank className="w-3 h-3 mr-1" />
              <span>Assign to Goal</span>
            </button>
          </div>
        </div>
      </div>
      <TransactionDetailsModal
        transaction={transaction}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
      {isAssignDialogOpen && (
        <AssignToGoalDialog 
          isOpen={isAssignDialogOpen}
          onClose={() => setIsAssignDialogOpen(false)}
          transaction={transaction as any}
        />
      )}
    </>
  );
}
